import styled from 'styled-components';
import { formatUsdCents } from '../../lib/pricing/formatMoney';
import type { PriceBookSku } from '../../lib/pricebook/pricebook';

const Wrap = styled.section`
  display: grid;
  gap: 1rem;
`;

const TierCard = styled.button<{ $selected: boolean }>`
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.primary : theme.colors.gray200};
  background: ${({ theme, $selected }) =>
    $selected ? `${theme.colors.primary}10` : theme.colors.gray050};
  border-radius: 14px;
  padding: 1rem;
  text-align: left;
  display: grid;
  gap: 0.65rem;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const TierTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
`;

const TierName = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.gray900};
`;

const TierPrice = styled.p`
  margin: 0;
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
`;

const TierMeta = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  line-height: 1.45;
  font-size: 0.92rem;
`;

function readHours(metadata: Record<string, unknown>): string | null {
  const hours = metadata.deliveryHours as { min?: number; max?: number } | undefined;
  if (!hours || typeof hours.min !== 'number' || typeof hours.max !== 'number') {
    return null;
  }
  return `${hours.min}-${hours.max} delivery hours`;
}

function readCadence(metadata: Record<string, unknown>): string | null {
  const cadenceType =
    typeof metadata.clientSuccessCadenceType === 'string'
      ? metadata.clientSuccessCadenceType.trim()
      : '';
  const meetingsRaw = metadata.clientSuccessMeetingsPerMonth;
  const meetings =
    typeof meetingsRaw === 'number'
      ? meetingsRaw
      : typeof meetingsRaw === 'string' && meetingsRaw.trim()
        ? Number.parseInt(meetingsRaw, 10)
        : null;
  if (cadenceType) {
    const label = cadenceType.charAt(0).toUpperCase() + cadenceType.slice(1).toLowerCase();
    if (typeof meetings === 'number' && Number.isFinite(meetings) && meetings > 0) {
      return `${label} client-success cadence · ${meetings} meetings/month`;
    }
    return `${label} client-success cadence`;
  }
  const cadence = metadata.cadence;
  if (typeof cadence === 'string' && cadence.trim()) return cadence;
  const fallback = metadata.clientSuccessCadence;
  return typeof fallback === 'string' && fallback.trim() ? fallback : null;
}

function readTermNote(metadata: Record<string, unknown>): string | null {
  const explicit = metadata.recommendedTermUiCopy;
  if (typeof explicit === 'string' && explicit.trim()) return explicit;
  const minTerm = metadata.recommendedMinTermMonths ?? metadata.minTermMonths;
  if (typeof minTerm === 'number' && minTerm > 0) {
    return `${minTerm} month minimum term`;
  }
  return null;
}

type TierSelectorProps = {
  readonly tiers: readonly PriceBookSku[];
  readonly selectedSkuId?: string | null;
  readonly onSelect: (skuId: string) => void;
};

export default function TierSelector({
  tiers,
  selectedSkuId,
  onSelect,
}: TierSelectorProps) {
  if (!tiers.length) return null;

  return (
    <Wrap>
      {tiers.map((tier) => {
        const metadata = tier.metadata as Record<string, unknown>;
        const hours = readHours(metadata);
        const cadence = readCadence(metadata);
        const term = readTermNote(metadata);

        return (
          <TierCard
            key={tier.skuId}
            $selected={selectedSkuId === tier.skuId}
            type="button"
            onClick={() => onSelect(tier.skuId)}
            aria-pressed={selectedSkuId === tier.skuId}
          >
            <TierTitleRow>
              <TierName>{tier.name}</TierName>
              <TierPrice>{formatUsdCents(tier.amountCents)}/mo</TierPrice>
            </TierTitleRow>
            <TierMeta>{tier.description}</TierMeta>
            {hours ? <TierMeta>{hours}</TierMeta> : null}
            {cadence ? <TierMeta>{cadence}</TierMeta> : null}
            {term ? <TierMeta>{term}</TierMeta> : null}
          </TierCard>
        );
      })}
    </Wrap>
  );
}
